<script lang="ts" setup>
import { ElNotification } from "element-plus";
import { ref, reactive } from "vue";
import { Close, Plus } from "@element-plus/icons-vue";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";
import { isUserPermitted } from "~/utils/permissionHelper";
import { useSettingsStore } from "~/stores/settingsStore";
import { Partner } from "~/projects/crm/types";
import { usePartnerStore } from "~/projects/crm/stores/partnerStore";

definePageMeta({ layout: false });

const props = defineProps({
  showEditModalHeader: { type: Boolean, default: true },
});

const partnerStore = usePartnerStore();
const configStore = useConfigStore();
const settingsStore = useSettingsStore();

onMounted(async () => {
  await settingsStore.getCrmSettings();
  await settingsStore.getPartnerCategories();
});

const formRef = ref<ElementUIFormRef | null>(null);

const formData = reactive<Partial<Partner>>({
  name: "",
  phone: "",
  email: "",
  type: "",
  category_ids: [],
  address: "",
  description: "",
});

const rules = reactive<FormRuleType>({
  name: [{ required: true, message: "Name is required", trigger: "blur" }],
  email: [{ type: "email", message: "Invalid email format", trigger: "blur" }],
  phone: [{ required: false, trigger: "blur" }],
  type: [{ required: false, trigger: "blur" }],
  category_ids: [{ required: false, trigger: "change" }],
  address: [{ required: false, trigger: "blur" }],
  description: [{ required: false, trigger: "blur" }],
});

const multipartFormData = new FormData();

const createPartner = () => {
  if (!formRef.value) return;

  formRef.value.validate(async (valid) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all required fields",
        type: "error",
      });
      return;
    }

    // Clear and rebuild FormData
    multipartFormData.forEach((_, key) => multipartFormData.delete(key));

    for (const [key, value] of Object.entries(formData)) {
      if (value === null || value === undefined || value === "") continue;

      if (Array.isArray(value)) {
        value.forEach((val) => multipartFormData.append(`${key}[]`, val));
      } else {
        multipartFormData.set(key, value as any);
      }
    }

    const partner = await partnerStore.createPartner(multipartFormData);
    if (partner) {
      ElNotification({
        title: "Success",
        message: "New Partner created",
        type: "success",
      });
      partnerStore.setCreateModalVisibility(false);
      await partnerStore.getPartners();
      await partnerStore.getSimplePartners();
    }
  });
};
</script>

<template>
  <EditModalHeader v-if="props.showEditModalHeader">
    <template #title>Create Partner</template>
    <template #svg><Svgs name="pen" /></template>
  </EditModalHeader>

  <el-form
    ref="formRef"
    :model="formData"
    :rules="rules"
    class="space-y-5"
    @submit.prevent="createPartner"
  >
    <div class="feature-filter-grid-1">
      <el-form-item prop="name">
        <FormLabel :required="true">Name</FormLabel>
        <el-input
          v-model="formData.name"
          placeholder="Enter partner name"
          clearable
        />
      </el-form-item>

      <el-form-item prop="phone">
        <FormLabel>Phone</FormLabel>
        <el-input
          v-model="formData.phone"
          placeholder="Enter phone number"
          clearable
        />
      </el-form-item>

      <el-form-item prop="email">
        <FormLabel>Email</FormLabel>
        <el-input
          v-model="formData.email"
          placeholder="Enter email address"
          clearable
        />
      </el-form-item>

      <el-form-item prop="type">
        <FormLabel>Type</FormLabel>
        <el-select
          v-model="formData.type"
          filterable
          clearable
          class="w-full"
          placeholder="Select type"
        >
          <el-option
            v-for="(type, index) in settingsStore.crmSettings
              ?.partner_type_list"
            :key="index"
            :label="type"
            :value="type"
          />
        </el-select>
      </el-form-item>

      <el-form-item prop="category_ids">
        <FormLabel>Category</FormLabel>
        <el-select
          v-model="formData.category_ids"
          filterable
          clearable
          class="w-full"
          placeholder="Select category"
          multiple
          collapse-tags
        >
          <el-option
            v-for="category in settingsStore.partnerCategories"
            :key="category.id"
            :label="category.name"
            :value="category.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item prop="address">
        <FormLabel>Address</FormLabel>
        <el-input
          v-model="formData.address"
          placeholder="Enter address"
          clearable
        />
      </el-form-item>

      <el-form-item prop="description">
        <FormLabel>Description</FormLabel>
        <el-input
          v-model="formData.description"
          type="textarea"
          placeholder="Enter description"
          clearable
          rows="3"
        />
      </el-form-item>
    </div>

    <div class="feature-filter-submit">
      <NavButton
        type="button"
        @click="partnerStore.setCreateModalVisibility(false)"
      >
        Cancel
      </NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.ORGANIZATION_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>
</template>
